import type { Size, Transform, Vec2 } from './geometry';
import type { ApplicationId, ConnectionId, ObjectId, WorkspaceId } from './ids';

export interface ObjectMetadata {
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly tags: readonly string[];
}

/**
 * Fields shared by every object that lives in the spatial world.
 *
 * Transform is object-local: position/rotation/scale relative to the parent's
 * frame (or the world for roots). `size` is the unscaled content box; rendering
 * applies `transform.scale` on top of it so scale and resize stay distinct.
 */
interface SpatialObjectBase {
  readonly id: ObjectId;
  readonly workspaceId: WorkspaceId;
  readonly name: string;
  readonly transform: Transform;
  readonly size: Size;
  /** Larger values render on top. */
  readonly zIndex: number;
  /** Container membership. `null` means the object is a root of the scene. */
  readonly parentId: ObjectId | null;
  /** Locked objects can be selected and inspected but not moved, resized or rotated. */
  readonly locked: boolean;
  readonly visible: boolean;
  readonly metadata: ObjectMetadata;
}

/** Fields shared by objects that own children. */
interface ContainerBase extends SpatialObjectBase {
  readonly childIds: readonly ObjectId[];
  /** Collapsed containers show only their header; children are hidden and inert. */
  readonly collapsed: boolean;
  /** Size to return to on expand; `null` while expanded. */
  readonly expandedSize: Size | null;
}

export type NoteColor = 'paper' | 'sand' | 'moss' | 'sky' | 'clay';

/** A plain text note pinned in space. */
export interface SpatialNote extends SpatialObjectBase {
  readonly kind: 'note';
  readonly content: string;
  readonly color: NoteColor;
}

export type WindowState = 'normal' | 'minimized' | 'maximized';

/** Serialisable state owned by an application instance. */
export type ApplicationState = Readonly<Record<string, string | number | boolean | null>>;

/** Position and size to return to after a maximize/minimize. */
export interface WindowFrame {
  readonly position: Vec2;
  readonly size: Size;
}

/** A running application instance rendered as a window in the world. */
export interface SpatialWindow extends SpatialObjectBase {
  readonly kind: 'window';
  readonly applicationId: ApplicationId;
  readonly windowState: WindowState;
  readonly applicationState: ApplicationState;
  /** Frame before the current maximize; `null` when the window is in its normal state. */
  readonly previousFrame: WindowFrame | null;
}

/** A visual container that moves its children together. */
export interface SpatialGroup extends ContainerBase {
  readonly kind: 'group';
}

/** A named container with file-system semantics; children are typically files. */
export interface SpatialFolder extends ContainerBase {
  readonly kind: 'folder';
}

/** A reference to a file. Phase 3 stores descriptive metadata only, never contents. */
export interface SpatialFile extends SpatialObjectBase {
  readonly kind: 'file';
  readonly fileName: string;
  readonly mimeType: string;
  readonly byteSize: number;
}

/** A terminal session placeholder: a working directory and a scrollback buffer. */
export interface SpatialTerminal extends SpatialObjectBase {
  readonly kind: 'terminal';
  readonly cwd: string;
  readonly scrollback: readonly string[];
}

/** A link to a URL. Only http(s) URLs are accepted by validation. */
export interface SpatialBookmark extends SpatialObjectBase {
  readonly kind: 'bookmark';
  readonly url: string;
  readonly title: string;
  readonly description: string;
}

/** An image. `src` must be an https: or data:image URL; validation rejects anything else. */
export interface SpatialImage extends SpatialObjectBase {
  readonly kind: 'image';
  readonly src: string;
  readonly alt: string;
  readonly naturalSize: Size | null;
}

/** An application shortcut: launching it creates a window for `applicationId`. */
export interface SpatialApplication extends SpatialObjectBase {
  readonly kind: 'application';
  readonly applicationId: ApplicationId;
}

export type SpatialObject =
  | SpatialNote
  | SpatialWindow
  | SpatialGroup
  | SpatialFolder
  | SpatialFile
  | SpatialTerminal
  | SpatialBookmark
  | SpatialImage
  | SpatialApplication;

export type SpatialObjectKind = SpatialObject['kind'];
export type SpatialContainer = SpatialGroup | SpatialFolder;
export type ContainerKind = SpatialContainer['kind'];

export const isContainer = (obj: SpatialObject): obj is SpatialContainer =>
  obj.kind === 'group' || obj.kind === 'folder';

/**
 * Semantic relationship carried by a connection. Rendering derives arrowheads and
 * dashes from the type; graph rules derive which types must stay acyclic.
 */
export type ConnectionType = 'depends-on' | 'contains' | 'references' | 'related-to' | 'blocks' | 'linked-to';

export const CONNECTION_TYPES: readonly ConnectionType[] = [
  'linked-to',
  'related-to',
  'references',
  'depends-on',
  'blocks',
  'contains',
];

export const isConnectionType = (v: unknown): v is ConnectionType =>
  typeof v === 'string' && (CONNECTION_TYPES as readonly string[]).includes(v);

/** A directed link from `sourceId` to `targetId`. */
export interface Connection {
  readonly id: ConnectionId;
  readonly workspaceId: WorkspaceId;
  readonly sourceId: ObjectId;
  readonly targetId: ObjectId;
  readonly type: ConnectionType;
  readonly label: string;
}
